import React, { useEffect, useMemo, useRef, useState } from "react";
import Rajneeti1 from "../../videos/Rajneeti/Rajneeti1.mp4";
import { NavLink as RouterLink } from "react-router-dom";
/* ✅ ONLY HERO VIDEO */
const HRA_ASSETS = { hero: { type: "mp4", video: Rajneeti1 } };

/* ---------- Widget Card (shared style) ---------- */
function HraServiceWidget({
  icon = "🔷",
  title,
  desc,
  href,
  to,
  btnName
}) {
  return (
    <div className="hra-widget" role="article">
      <div className="hra-widget__icon" aria-hidden="true">
        {icon}
      </div>

      <h4 className="hra-widget__title">{title}</h4>

      <p className="hra-widget__desc">{desc}</p>

      {to ? (
        <RouterLink to={to} className="hra-widget__cta">
          {btnName}
          <span className="hra-widget__ctaArrow" aria-hidden>
            →
          </span>
        </RouterLink>
      ) : (
        <button
          type="button"
          className="hra-widget__cta"
          onClick={() =>
            window.open(href, "_blank", "noopener,noreferrer")
          }
        >
          {btnName}
          <span className="hra-widget__ctaArrow" aria-hidden>
            →
          </span>
        </button>
      )}
    </div>
  );
}

/* ---------- Section Card ---------- */
function HraSection({ title, children }) {
  return (
    <div className="row g-4 mt-4">
      <div className="col-12">
        <div className="hra-card hra-div-text">
          {title ? <h3 className="white mb-2">{title}</h3> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

function HinduRajneetiAcademy() {
  /* ✅ VIDEO controls */
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const topVideoRef = useRef(null);

  useEffect(() => {
    const v = topVideoRef.current;
    if (!v) return;

    v.muted = true;

    const playPromise = v.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      setIsPlaying(!v.paused);
    }
  }, []);

  useEffect(() => {
    const v = topVideoRef.current;
    if (v) v.muted = isMuted;
  }, [isMuted]);

  const togglePlayPause = async () => {
    const v = topVideoRef.current;
    if (!v) return;

    if (v.paused) {
      try {
        await v.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => setIsMuted((m) => !m);

  /* ✅ CONTENT */
  const divisions = useMemo(
    () => [
      {
        key: "intro",
        title: "Introduction",
        content: (
          <>
            <p className="white">
              The Hindu Rajneeti Academy is a civic and leadership education platform created
              to empower Hindu Americans and pro-Hindu allies with a strong understanding of the U.S.
              civic, political, and governance systems.
            </p>
            <p className="white">
              Rooted in timeless values of Sanatana Dharma—Dharma (duty), Seva (service), Satya
              (truth), and pluralism—the Academy connects heritage and identity with modern democratic
              participation.
            </p>
            <p className="white">
              <strong>Non-partisan. Independent. Inclusive.</strong>
            </p>
            <p className="white">
              HRA welcomes participants from all backgrounds who believe in constitutional values,
              respectful dialogue, and constructive civic engagement.
            </p>
          </>
        ),
      },

      {
        key: "overview",
        title: "Overview & Purpose",
        content: (
          <p className="white">
            HRA exists to build a stronger, more informed, and more effective Hindu American civic
            presence across the United States.
          </p>
        ),
      },

      {
        key: "goals",
        title: "Our Goals",
        content: (
          <ul className="white arrow-list">
            <li>Strengthen civic and political literacy in the community</li>
            <li>Develop informed voters, volunteers, advocates, and candidates</li>
            <li>Prepare ethical leaders for public service and governance</li>
            <li>Expand Hindu representation at local, state, and federal levels</li>
            <li>
              Improve public understanding and protect the community from misinformation and
              misrepresentation
            </li>
          </ul>
        ),
      },

      {
        key: "online",
        title: "Online Courses & Webinars",
        content: (
          <p className="white">
            HRA offers structured online learning designed for real-world impact—ideal for students,
            professionals, community leaders, and aspiring public servants.
          </p>
        ),
      },

      /* ---------- Course Modules ---------- */
      {
        key: "module-civics",
        title: "Understanding U.S. Civic Structure",
        content: (
          <>
            <p className="white">Learn how American democracy works and where civic power truly lives.</p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>Constitution, Bill of Rights, and core civic freedoms</li>
              <li>Federal vs. state vs. local government responsibilities</li>
              <li>How laws are made and implemented</li>
              <li>City councils, mayors, school boards, state legislatures, Congress</li>
              <li>Courts, commissions, agencies, and community influence points</li>
            </ul>
          </>
        ),
      },

      {
        key: "module-campaigns",
        title: "How Political Campaigns Work",
        content: (
          <>
            <p className="white">
              A practical guide to what it takes to win elections—and how to contribute responsibly.
            </p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>Campaign structure and key roles</li>
              <li>Fundraising basics and compliance awareness</li>
              <li>Voter outreach: canvassing, phones, digital, events</li>
              <li>Messaging, debates, and candidate positioning</li>
              <li>Election timelines and grassroots ground operations</li>
            </ul>
          </>
        ),
      },

      {
        key: "module-pathways",
        title: "School Board to Congress: Pathways for Hindus",
        content: (
          <>
            <p className="white">
              A roadmap for Hindu Americans to enter public service and grow into leadership.
            </p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>Why local offices matter most (school boards, commissions, councils)</li>
              <li>Choosing the right entry point based on your skills and mission</li>
              <li>Building trust, credibility, and coalitions</li>
              <li>From local to state to federal: realistic leadership pathways</li>
              <li>Lessons from successful leaders and campaigns</li>
            </ul>
          </>
        ),
      },

      {
        key: "module-media",
        title: "Media & Narrative Building",
        content: (
          <>
            <p className="white">Own the narrative. Communicate clearly. Respond with strategy.</p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>How mainstream, ethnic, and digital media operate</li>
              <li>Countering misinformation and harmful stereotypes</li>
              <li>Storytelling rooted in values and facts</li>
              <li>Writing op-eds, statements, and press releases</li>
              <li>Social media strategy and crisis communication basics</li>
            </ul>
          </>
        ),
      },

      {
        key: "module-volunteer",
        title: "Volunteer & Campaign Training",
        content: (
          <>
            <p className="white">
              Turn passion into effective action through disciplined grassroots engagement.
            </p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>Voter registration and Get-Out-The-Vote (GOTV) operations</li>
              <li>Event organizing, outreach, and community mobilization</li>
              <li>Ethical advocacy and legal boundaries</li>
              <li>Building alliances with other communities</li>
              <li>Measuring results and sustaining momentum</li>
            </ul>
          </>
        ),
      },

      {
        key: "module-leadership",
        title: "Leadership for Public Office",
        content: (
          <>
            <p className="white">Advanced training for those preparing to run or serve in government.</p>
            <p className="white">
              <strong>What you’ll learn:</strong>
            </p>
            <ul className="white arrow-list">
              <li>Ethical leadership and public accountability</li>
              <li>Public speaking, interviews, and debate readiness</li>
              <li>Governance fundamentals and policy learning skills</li>
              <li>Decision-making under pressure</li>
              <li>Balancing identity, values, and broad representation</li>
            </ul>
          </>
        ),
      },

      {
        key: "library",
        title: "Course Library (On-Demand)",
        content: (
          <>
            <p className="white">
              HRA maintains a growing library of recorded courses and sessions so members can learn anytime,
              anywhere.
            </p>
            <p className="white">
              <strong>Features</strong>
            </p>
            <ul className="white arrow-list">
              <li>Self-paced learning</li>
              <li>Short modules and deep-dive masterclasses</li>
              <li>Expert panels, case studies, and leadership interviews</li>
              <li>Ideal for beginners and advanced learners</li>
            </ul>
          </>
        ),
      },

      {
        key: "webinars",
        title: "Live Webinars & Upcoming Trainings",
        content: (
          <>
            <p className="white">Join live online sessions featuring:</p>
            <ul className="white arrow-list">
              <li>Elected officials and policy leaders</li>
              <li>Campaign strategists and legal experts</li>
              <li>Media professionals and community organizers</li>
              <li>Q&amp;A, networking, and timely election-cycle topics</li>
            </ul>
            <p className="white">Check back often for the latest schedule and registration links.</p>
          </>
        ),
      },

      {
        key: "certification",
        title: "Certification Programs",
        content: (
          <>
            <p className="white">
              HRA offers certification tracks to recognize training, service, and leadership readiness.
            </p>
            <h4 className="white mt-3"><strong>Certification Tracks</strong></h4>
            <ul className="white arrow-list">
              <li>Certified Civic Ambassador</li>
              <li>Certified Campaign Volunteer</li>
              <li>Certified Community Organizer</li>
              <li>Certified Political Leadership Fellow</li>
            </ul>
            <h4 className="white mt-3"><strong>Benefits</strong></h4>
            <ul className="white arrow-list">
              <li>Official recognition and credentials</li>
              <li>Eligibility for leadership roles within the A4H ecosystem</li>
              <li>Priority access to advanced trainings and strategy sessions</li>
              <li>Enhanced readiness for advocacy and public service</li>
            </ul>
          </>
        ),
      },

      {
        key: "vision",
        title: "Our Vision",
        content: (
          <p className="white">
            A future where Hindu Americans are informed, engaged, respected, and represented at every level
            of American democracy—from School Boards to Congress and beyond.
          </p>
        ),
      },

      /* ---------- Ready to begin? (2x2 + FIXED HEIGHT) ---------- */
      {
        key: "cta-widgets-begin",
        title: "Ready to begin?",
        content: (
          <div className="container p-0">
            <div className="row g-4">
              <div className="col-6 col-md-6">
                <div className="hra-begin-fixed">
                  <HraServiceWidget
                    icon="🎓"
                    title="Enroll for A4H Rajneeti Academy"
                    btnName="ENROLL/REGISTER"
                    href="https://tinyurl.com/A4HRAJNEETIACADEMYONLINE"
                    desc="Start learning through structured courses and participate in live weekly sessions with policy leaders, campaign experts, and community organizers."
                  />
                </div>
              </div>

              <div className="col-6 col-md-6">
                <div className="hra-begin-fixed">
                  <HraServiceWidget
                    icon="📚"
                    title="Explore On-Demand Library"
                    btnName="SESSIONS LIBRARY"
                    href="https://www.youtube.com/@americans4hindusa4h"
                    desc="Watch recorded talks and masterclasses anytime—ideal for busy schedules."
                  />
                </div>
              </div>

            
              
            </div>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <style>{`
        /* ==== THEME VARS (tweak your transparent fill here) ==== */
        :root{
          --hra-fill: 11 58 150;         /* base color as R G B */
          --hra-fill-alpha: 0.35;        /* transparency 0.0 – 1.0 */
        }

        html, body { overflow-x: hidden; }

        /* ✅ Fixed height wrapper ONLY for Ready to begin blocks */
        .hra-begin-fixed{
          height: 420px;          /* change this value if you want taller/shorter */
        }
        .hra-begin-fixed .hra-widget{
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .hra-begin-fixed .hra-widget__desc{
          flex: 1 1 auto;
          min-height: 0 !important;
        }
        .hra-begin-fixed .hra-widget__cta{
          margin-top: auto;
        }

        /* ===== Layout / heading ===== */
        .block.color-scheme-4{ padding-left:0!important; padding-right:0!important; overflow-x:hidden; }
        .hra-topWrap{ max-width:980px; margin:0 auto 18px; padding:6px 6px 0; text-align:center; }
        .hra-topTitle{ margin:0; line-height:1.1; font-size:clamp(28px,4vw,44px); font-weight:800; letter-spacing:0.01em; }
        .hra-topIntro{ margin:10px auto 0; max-width:860px; opacity:.96; font-size:16px; line-height:1.55; }

        /* ===== Full-width video ===== */
        .hra-fullVideo{
          width: 90%;
  max-width: 1600px;
  margin: 40px auto;
  height: auto;
  background: #000;
  position: relative;
  overflow: visible;
}
        .hra-fullVideo video{ width: 100%;
  height: auto;
  object-fit: contain;
}
        .hra-videoControls{ position:absolute; top:auto; right:auto; bottom:16px; left:16px; display:flex; gap:10px; z-index:3; }
        .hra-ctrlBtn{
          border:1px solid rgba(255,255,255,.22); background:rgba(0,0,0,.45); color:#fff;
          border-radius:999px; padding:10px 14px; font-size:13px; letter-spacing:.02em;
          backdrop-filter:blur(10px); cursor:pointer; white-space:nowrap;
        }
        .hra-ctrlBtn:hover{ background:rgba(0,0,0,.6); }

        /* ===== Section cards ===== */
        .hra-card{ border-radius:18px; overflow:hidden; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); position:relative; }
        .hra-div-text{ padding:18px; text-align:left; }

        /* ===== Force BLACK text below video EXCEPT h3 ===== */
        .hra-after-video p,
        .hra-after-video li,
        .hra-after-video ul,
        .hra-after-video ol,
        .hra-after-video span,
        .hra-after-video div { color:#000 !important; }
        /* .hra-after-video h3 { color:inherit !important; } */

        /* ===== Widget grid ===== */
        .hra-widgets-grid{ display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; }
        @media (max-width: 992px){ .hra-widgets-grid{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width: 600px){ .hra-widgets-grid{ grid-template-columns:1fr; } }

        /* ===== Widget card base ===== */
        .hra-widget{
          position:relative; border-radius:22px; background:#fff; border:1px solid rgba(0,0,0,.12);
          box-shadow:0 8px 24px rgba(0,0,0,.08); padding:22px 22px 20px; text-align:left; overflow:hidden;
          transition:transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease; isolation:isolate;
        }
        .hra-widget:hover{ transform:translateY(-4px); box-shadow:0 14px 40px rgba(0,0,0,.18); border-color:rgba(0,0,0,.18); }

        /* ---- Hover animation: glow + TRANSPARENT color fill sweep ---- */
        .hra-widget::before{
          content:""; position:absolute; inset:0; border-radius:inherit;
          background:radial-gradient(120% 140% at 50% 10%, rgba(60,90,210,.20), rgba(35,60,170,.55));
          opacity:0; transition:opacity 200ms ease; z-index:-2;
        }
        .hra-widget::after{
          content:""; position:absolute; inset:0; border-radius:inherit;
          background: rgb(var(--hra-fill) / var(--hra-fill-alpha));
          transform:scaleX(0);
          transform-origin:0% 50%;
          transition:transform 380ms cubic-bezier(.22,.75,.25,1);
          z-index:-1;
        }
        .hra-widget:hover::before{ opacity:1; }
        .hra-widget:hover::after{ transform:scaleX(1); }

        /* ---- Micro-interactions ---- */
        .hra-widget__icon{
          width:54px; height:54px; border-radius:14px; display:grid; place-items:center; font-size:28px; margin-bottom:12px;
          background:#0b3a96; color:#fff; box-shadow:0 6px 20px rgba(11,58,150,.35);
          transition:transform 260ms ease, filter 220ms ease;
        }
        .hra-widget:hover .hra-widget__icon{ transform:translateY(-2px) rotate(-1deg); filter:brightness(1.05); }

        .hra-widget__title{ margin:0 0 8px 0; font-weight:800; font-size:22px; letter-spacing:.2px; color:#0e1a2b; transition:color 200ms ease; }
        .hra-widget__desc{ margin:0 0 16px 0; color:#1e2430; line-height:1.55; min-height:96px; transition:color 200ms ease; }

        .hra-widget__cta{
          position:relative; display:inline-flex; align-items:center; gap:8px; padding:10px 16px; border-radius:999px; border:0;
          background:#0b3a96; color:#fff; font-weight:700; cursor:pointer;
          transition:transform 200ms ease, box-shadow 200ms ease, background 200ms ease, color 200ms ease;
          box-shadow:0 10px 26px rgba(11,58,150,.35); overflow:hidden;
        }
        .hra-widget__cta:hover{ transform:translateY(-2px); box-shadow:0 16px 46px rgba(11,58,150,.45); }
        .hra-widget__ctaArrow{ transform:translateX(0); transition:transform 220ms ease; }
        .hra-widget__cta:hover .hra-widget__ctaArrow{ transform:translateX(3px); }

        .hra-widget:hover .hra-widget__cta::after{
          content:""; position:absolute; top:0; bottom:0; left:-60%; width:45%;
          background:linear-gradient(100deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
          transform:skewX(-18deg);
          animation: hraShine 800ms ease forwards;
        }
        .section-divider{
  width:100%;
  max-width:900px;
  margin:32px auto 38px;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:18px;
}

.section-divider::before,
.section-divider::after{
  content:"";
  flex:1;
  height:3px;

  background: linear-gradient(
    to right,
    transparent,
    #1a2a5a,
    #d4af37,
    transparent
  );

  border-radius:10px;

  box-shadow:
    0 2px 6px rgba(0,0,0,0.18);
}

.section-divider span{
  font-size:26px;
  color:#1a2a5a;
  text-shadow:0 2px 6px rgba(0,0,0,0.15);

  animation: dividerGlow 2.5s ease-in-out infinite;
}
        @keyframes hraShine{
          0% { left:-60%; opacity:.0; }
          35% { opacity:.9; }
          100% { left:130%; opacity:0; }
        }

        .hra-widget:hover .hra-widget__title,
        .hra-widget:hover .hra-widget__desc{ color:#fff; }
        .hra-widget:hover .hra-widget__cta{ background:#fff; color:#0b3a96; box-shadow:0 12px 36px rgba(255,255,255,.25); }

        /* ====== CTA ALIGNMENT FOR "READY TO BEGIN?" (modifier) ====== */
        .hra-widgets-grid.is-begin { align-items: stretch; grid-auto-rows: 1fr; }
        .hra-widgets-grid.is-begin .hra-widget { display:flex; flex-direction:column; height:100%; }
        .hra-widgets-grid.is-begin .hra-widget__desc { flex-grow:1; min-height:0 !important; }
        .hra-widgets-grid.is-begin .hra-widget__cta { margin-top:auto; }
.hra-btn-icon{
  display:none;
}

.hra-btn-text{
  display:inline;
}
        /* =========================
   MOBILE VERSION ONLY
   ========================= */
@media (max-width: 768px) {

  /* Reduce gap above header */
.hra-topWrap{
  padding-top: 0 !important;
  margin-top: -70px !important;
  margin-bottom: 6px !important;
}

/* Keep title in single line */
.hra-topTitle{
  font-size: 30px !important;
  line-height: 1.1 !important;
}

.hra-topTitle .uppercase{
  display: block !important;
  font-size: 20px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.hra-topTitle .lowercase{
  font-size: 13px !important;
  margin-top: 2px !important;
}

/* Reduce divider gap */
.section-divider{
  margin: 20px auto 20px !important;
}

  .section-divider span{
    font-size: 18px !important;
  }
.hra-topIntro{
  font-size: 15px !important;
  line-height: 1.35 !important;
  padding: 0 6px !important;
  margin-top: 2px !important;
}
  /* Video */
  .hra-fullVideo{
    width: 100% !important;
    margin: 20px auto !important;
    border-radius: 0 !important;
  }

  .hra-videoControls{
    bottom: 6px !important;
    left: 6px !important;
    gap: 4px !important;
  }

  .hra-btn-text{
  display:none;
}

.hra-btn-icon{
  display:inline;
  font-size:20px;
  line-height:1;
}

.hra-videoControls{
  bottom:6px !important;
  left:6px !important;
  gap:4px !important;
}

.hra-ctrlBtn{
  background:transparent !important;
  border:none !important;
  box-shadow:none !important;
  backdrop-filter:none !important;
  padding:0 !important;
  width:auto !important;
  height:auto !important;
  min-width:auto !important;
  border-radius:0 !important;
  color:#fff;
  cursor:pointer;
}

.hra-ctrlBtn:hover,
.hra-ctrlBtn:focus,
.hra-ctrlBtn:active{
  background:transparent !important;
  box-shadow:none !important;
}

  /* Content cards */
  .hra-card{
    border-radius: 14px !important;
  }

  .hra-div-text{
    padding: 14px !important;
  }

  .hra-div-text h3{
    font-size: 15px !important;
    margin-bottom: 10px !important;
  }

  .hra-div-text p,
  .hra-div-text li{
    font-size: 14px !important;
    line-height: 1.65 !important;
  }

  /* Reduce section spacing */
  .row.g-4.mt-4{
    margin-top: -16px !important;

  }

  .col-6.col-md-6{
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  /* Smaller card height */
  .hra-begin-fixed{
    height: 140px !important;
  }

  .hra-widget{
    padding: 8px !important;
    border-radius: 12px !important;
    min-height: 130px !important;
  }

  .hra-widget__icon{
    width: 28px !important;
    height: 28px !important;
    font-size: 14px !important;
    margin-bottom: 4px !important;
  }

  .hra-widget__title{
    font-size: 11px !important;
    line-height: 1.2 !important;
    margin-bottom: 6px !important;
    text-align: center !important;
  }

  /* REMOVE DESCRIPTION */
  .hra-widget__desc{
    display: none !important;
  }

  .hra-widget__cta{
  width: 100%;
  padding: 6px 4px !important;
  font-size: 8px !important;
  margin-top: auto !important;

  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}

.hra-widget__ctaArrow{
  display: none !important;
}

  /* Container width */
  .container{
    padding-left: 12px !important;
    padding-right: 12px !important;
  }
}
        
      `}</style>

      <section>
        <div className="block color-scheme-4">
          {/* Heading */}
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="hra-topWrap">
                  <h1 className="hra-topTitle white head-title">
                    <span
                      className="uppercase"
                      style={{ display: "block" }}
                    >
                      Hindu Rajneeti Academy
                    </span>

                    <span
                      className="lowercase"
                      style={{ display: "block", marginTop: "6px" }}
                    >
                      (Leadership Development)
                    </span>
                                      <div className="section-divider">
                    <span>✦ ✦ ✦</span>
                  </div>
                  </h1>
                  <p className="hra-topIntro white">
                    The Hindu Rajneeti Academy is a civic and leadership education platform
                    empowering Hindu Americans and pro-Hindu allies with a strong understanding of U.S.
                    civic and governance systems.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Full-width video */}
          <div className="hra-fullVideo">
            <video
              ref={topVideoRef}
              src={HRA_ASSETS.hero.video}
              playsInline
              muted={isMuted}
              autoPlay
              loop
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            <div className="hra-videoControls">
  <button
    type="button"
    className="hra-ctrlBtn"
    onClick={togglePlayPause}
  >
    <span className="hra-btn-text">
      {isPlaying ? "Pause ⏸️" : "Play ▶️"}
    </span>
    <span className="hra-btn-icon">
      {isPlaying ? "⏸️" : "▶️"}
    </span>
  </button>

  <button
    type="button"
    className="hra-ctrlBtn"
    onClick={toggleMute}
  >
    <span className="hra-btn-text">
      {isMuted ? "Unmute 🔊" : "Mute 🔇"}
    </span>
    <span className="hra-btn-icon">
      {isMuted ? "🔊" : "🔇"}
    </span>
  </button>

            </div>
          </div>

          {/* All content AFTER video (black text except h3) */}
          <div className="hra-after-video">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-md-10 col-lg-9">
                  <article className="section-title-body">
                    {divisions.map((d) => (
                      <HraSection key={d.key} title={d.title}>
                        {d.content}
                      </HraSection>
                    ))}
                  </article>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}

export default HinduRajneetiAcademy;
